import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, userProfile, signOut, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  // Preview mode banner for unauthenticated users
  if (!loading && !user) {
    return (
      <header className="fixed top-0 left-0 right-0 z-50 glass bg-surface/80 backdrop-blur-cosmic border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
                <Icon name="Music" size={20} color="white" />
              </div>
              <span className="text-xl font-heading font-bold text-text-primary">
                AstroTone
              </span>
            </Link>

            {/* Preview Mode Badge */}
            <div className="flex items-center space-x-4">
              <div className="glass bg-accent/20 border border-accent/30 rounded-full px-3 py-1">
                <span className="text-xs font-medium text-accent">Preview Mode</span>
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="text-sm"
                >
                  Sign In
                </Button>
                <Button
                  onClick={() => navigate('/signup')}
                  className="text-sm"
                >
                  Sign Up
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>
    );
  }

  if (loading) {
    return (
      <header className="fixed top-0 left-0 right-0 z-50 glass bg-surface/80 backdrop-blur-cosmic border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="w-32 h-6 bg-surface rounded animate-pulse" />
            <div className="w-24 h-8 bg-surface rounded animate-pulse" />
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass bg-surface/80 backdrop-blur-cosmic border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
              <Icon name="Music" size={20} color="white" />
            </div>
            <span className="text-xl font-heading font-bold text-text-primary">
              AstroTone
            </span>
          </Link>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/dashboard"
              className={`flex items-center space-x-1 text-sm font-medium transition-colors ${
                isActive('/dashboard') || isActive('/')
                  ? 'text-accent' :'text-text-secondary hover:text-text-primary'
              }`}
            >
              <Icon name="Home" size={16} />
              <span>Dashboard</span>
            </Link>

            <Link
              to="/astrological-audio-player"
              className={`flex items-center space-x-1 text-sm font-medium transition-colors ${
                isActive('/astrological-audio-player')
                  ? 'text-accent' :'text-text-secondary hover:text-text-primary'
              }`}
            >
              <Icon name="Play" size={16} />
              <span>Audio Player</span>
            </Link>

            <Link
              to="/meet-someone-new"
              className={`flex items-center space-x-1 text-sm font-medium transition-colors ${
                isActive('/meet-someone-new')
                  ? 'text-accent' :'text-text-secondary hover:text-text-primary'
              }`}
            >
              <Icon name="Users" size={16} />
              <span>Meet Someone New</span>
            </Link>
          </nav>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="flex items-center space-x-2 text-sm focus:outline-none"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
                    <span className="text-white font-medium">
                      {userProfile?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <Icon name="ChevronDown" size={16} color="rgb(var(--color-text-secondary))" />
                </button>

                {isMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 glass bg-surface border border-white/20 rounded-xl shadow-lg overflow-hidden">
                    <div className="px-4 py-3 border-b border-white/10">
                      <p className="text-sm font-medium text-text-primary">
                        {userProfile?.full_name || 'User'}
                      </p>
                      <p className="text-xs text-text-secondary">
                        {user?.email}
                      </p>
                    </div>
                    
                    <div className="py-1">
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-white/5 transition-colors"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <div className="flex items-center space-x-2">
                          <Icon name="User" size={16} />
                          <span>Profile</span>
                        </div>
                      </Link>
                      
                      <Link
                        to="/settings"
                        className="block px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-white/5 transition-colors"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <div className="flex items-center space-x-2">
                          <Icon name="Settings" size={16} />
                          <span>Settings</span>
                        </div>
                      </Link>
                      
                      <button
                        onClick={handleSignOut}
                        className="w-full text-left px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-white/5 transition-colors"
                      >
                        <div className="flex items-center space-x-2">
                          <Icon name="LogOut" size={16} />
                          <span>Sign Out</span>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="text-sm"
                >
                  Sign In
                </Button>
                <Button
                  onClick={() => navigate('/signup')}
                  className="text-sm"
                >
                  Sign Up
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <Icon name="Menu" size={20} color="rgb(var(--color-text-primary))" />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-white/10">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link
                to="/dashboard"
                className={`block px-3 py-2 text-base font-medium rounded-lg transition-colors ${
                  isActive('/dashboard') || isActive('/')
                    ? 'text-accent bg-accent/10' :'text-text-secondary hover:text-text-primary hover:bg-white/5'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                Dashboard
              </Link>

              <Link
                to="/astrological-audio-player"
                className={`block px-3 py-2 text-base font-medium rounded-lg transition-colors ${
                  isActive('/astrological-audio-player')
                    ? 'text-accent bg-accent/10' :'text-text-secondary hover:text-text-primary hover:bg-white/5'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                Audio Player
              </Link>

              <Link
                to="/meet-someone-new"
                className={`block px-3 py-2 text-base font-medium rounded-lg transition-colors ${
                  isActive('/meet-someone-new')
                    ? 'text-accent bg-accent/10' :'text-text-secondary hover:text-text-primary hover:bg-white/5'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                Meet Someone New
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;